import type { PDFDocumentProxy } from 'pdfjs-dist';

// Shared pdf.js plumbing: the lazy loader, a ref-counted document cache (the
// canvas card and the reader share one parsed document), and a page thumbnail
// cache so a card renders each page once, not on every remount.

type PdfJs = typeof import('pdfjs-dist');

// pdf.js is ~1MB, so load it only when a PDF is actually shown
let pdfjsPromise: Promise<PdfJs> | null = null;
export function loadPdfJs(): Promise<PdfJs> {
  if (!pdfjsPromise) {
    pdfjsPromise = Promise.all([
      import('pdfjs-dist/legacy/build/pdf.mjs'),
      import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url'),
    ]).then(([pdfjs, worker]) => {
      pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
      return pdfjs as unknown as PdfJs;
    });
    pdfjsPromise.catch(() => { pdfjsPromise = null; });
  }
  return pdfjsPromise;
}

// ── Documents ───────────────────────────────────────────────────────────

interface DocEntry {
  promise: Promise<PDFDocumentProxy>;
  refs: number;
  timer: ReturnType<typeof setTimeout> | null;
}

const docs = new Map<string, DocEntry>();
/** Keep an unused document around briefly: closing the reader hands it back to the card. */
const DOC_IDLE_MS = 60_000;

/**
 * Open (or reuse) the document at `url`. Call `release` when done; the document
 * is destroyed once nothing has held it for a minute.
 */
export function acquirePdf(url: string): { promise: Promise<PDFDocumentProxy>; release: () => void } {
  let entry = docs.get(url);
  if (!entry) {
    // Range requests only: a card showing page 1 of a 40MB deck shouldn't download all of it
    const promise = loadPdfJs().then(
      (pdfjs) => pdfjs.getDocument({ url, disableAutoFetch: true, disableStream: true }).promise,
    );
    const created: DocEntry = { promise, refs: 0, timer: null };
    promise.catch(() => {
      if (docs.get(url) === created) docs.delete(url);
    });
    docs.set(url, created);
    entry = created;
  }
  const held = entry;
  if (held.timer) {
    clearTimeout(held.timer);
    held.timer = null;
  }
  held.refs++;

  let released = false;
  return {
    promise: held.promise,
    release: () => {
      if (released) return;
      released = true;
      held.refs--;
      if (held.refs > 0) return;
      held.timer = setTimeout(() => {
        if (docs.get(url) !== held || held.refs > 0) return;
        docs.delete(url);
        held.promise.then((d) => d.destroy()).catch(() => {});
      }, DOC_IDLE_MS);
    },
  };
}

/** Height / width of a page as displayed (rotation applied). */
export async function pdfPageAspect(doc: PDFDocumentProxy, pageNumber: number): Promise<number> {
  const page = await doc.getPage(pageNumber);
  const vp = page.getViewport({ scale: 1 });
  return vp.height / vp.width;
}

// ── Thumbnails ──────────────────────────────────────────────────────────

const MAX_THUMBS = 240;
const thumbs = new Map<string, Promise<string>>();

// A canvas full of PDF cards would otherwise render dozens of pages at once
const MAX_CONCURRENT_RENDERS = 2;
let activeRenders = 0;
const renderQueue: Array<() => void> = [];

function scheduleRender<T>(job: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const run = () => {
      activeRenders++;
      job().then(resolve, reject).finally(() => {
        activeRenders--;
        renderQueue.shift()?.();
      });
    };
    if (activeRenders < MAX_CONCURRENT_RENDERS) run();
    else renderQueue.push(run);
  });
}

/** Round a wanted pixel width up to a few fixed sizes so thumbnails get reused. */
export function thumbnailWidth(cssWidth: number): number {
  const ratio = typeof window === 'undefined' ? 1 : Math.min(window.devicePixelRatio || 1, 2);
  // A little headroom for zooming into the canvas
  const wanted = cssWidth * ratio * 1.25;
  return Math.min(1280, Math.max(240, Math.ceil(wanted / 160) * 160));
}

/**
 * A JPEG object URL of one page, `width` pixels wide. Cached per document,
 * page and width; failures are not cached so the next mount retries.
 */
export function pdfPageThumbnail(url: string, doc: PDFDocumentProxy, pageNumber: number, width: number): Promise<string> {
  const key = `${url}#${pageNumber}@${width}`;
  const cached = thumbs.get(key);
  if (cached) {
    // Refresh its place in the LRU order
    thumbs.delete(key);
    thumbs.set(key, cached);
    return cached;
  }

  const promise = scheduleRender(async () => {
    const page = await doc.getPage(pageNumber);
    const base = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: width / base.width });
    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No 2D context');
    await page.render({ canvasContext: ctx, viewport }).promise;
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.86));
    // Free the bitmap right away; iOS counts canvas memory against the tab
    canvas.width = 0;
    canvas.height = 0;
    if (!blob) throw new Error('Thumbnail encode failed');
    return URL.createObjectURL(blob);
  });

  thumbs.set(key, promise);
  promise.catch(() => {
    if (thumbs.get(key) === promise) thumbs.delete(key);
  });

  while (thumbs.size > MAX_THUMBS) {
    const [oldestKey, oldest] = thumbs.entries().next().value as [string, Promise<string>];
    thumbs.delete(oldestKey);
    oldest.then((objectUrl) => URL.revokeObjectURL(objectUrl)).catch(() => {});
  }
  return promise;
}
